import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button, Card, Flex, Radio, Space, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';

import {
  createQuestionAsAdmin,
  deleteQuestionAsAdmin,
  fetchAllQuestionModulesAsAdmin,
  fetchQuestionsAsAdmin,
  updateQuestionAsAdmin,
} from '@/apis';
import { QuestionModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { AnswerType, Industry, MessageType } from '@/enums';
import { useApiRequest, useAppFeedback } from '@/hooks';
import {
  AdminQuestion,
  AdminQuestionModule,
  CreateQuestionAsAdminRequest,
  UpdateQuestionAsAdminRequest,
} from '@/types/apis/admin';
import { getModuleLabel } from '@/utils/adminLabels';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

type AdminQuestionWithChildren = AdminQuestion & {
  children?: AdminQuestionWithChildren[];
};

const AdminQuestionsPage = () => {
  const { showMessage, showConfirm } = useAppFeedback();
  const [allQuestions, setAllQuestions] = useState<AdminQuestion[]>([]);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<AdminQuestion | null>(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('edit');
  const [deletingQuestion, setDeletingQuestion] = useState<string | null>(null);
  const [modules, setModules] = useState<AdminQuestionModule[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const pageSize = 50;
  // 一次載入全部題目（約一千多筆），篩選與計數都在前端完成
  const fetchAllLimit = 5000;

  // 檢查是否為sdgs模組
  const selectedModule = modules.find((m) => m.id === selectedModuleId);
  const isSDGsModule = selectedModule?.name.toLowerCase() === 'sdgs';
  const industries = Object.values(Industry);

  // 構建樹狀數據結構：主題為根節點，追問掛在 children
  const buildTreeData = (questions: AdminQuestion[]) => {
    const map = new Map<string, AdminQuestionWithChildren>();
    const roots: AdminQuestionWithChildren[] = [];

    questions.forEach((question) => {
      map.set(question.id, { ...question });
    });

    questions.forEach((question) => {
      const node = map.get(question.id)!;
      const parent = question.parentQuestionId
        ? map.get(question.parentQuestionId)
        : undefined;

      if (parent) {
        parent.children = [...(parent.children ?? []), node];
      } else {
        roots.push(node);
      }
    });

    return roots.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
  };

  const questionTree = useMemo(
    () => buildTreeData(allQuestions),
    [allQuestions],
  );

  // 各模組的主題數（不含追問）
  const moduleCounts = useMemo(() => {
    const counts = new Map<string, number>();
    questionTree.forEach((q) => {
      counts.set(q.module.id, (counts.get(q.module.id) ?? 0) + 1);
    });
    return counts;
  }, [questionTree]);

  // SDGs 模組內各產業的主題數
  const industryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    questionTree
      .filter((q) => q.module.id === selectedModuleId && q.industry)
      .forEach((q) => {
        const name = q.industry!.name;
        counts.set(name, (counts.get(name) ?? 0) + 1);
      });
    return counts;
  }, [questionTree, selectedModuleId]);

  const questions = useMemo(
    () =>
      questionTree.filter(
        (q) =>
          q.module.id === selectedModuleId &&
          (!isSDGsModule || q.industry?.name === selectedIndustry),
      ),
    [questionTree, selectedModuleId, selectedIndustry, isSDGsModule],
  );

  // Use useApiRequest for questions fetching
  const {
    loading: questionsLoading,
    error: questionsError,
    execute: loadQuestions,
  } = useApiRequest(
    async () => {
      const response = await fetchQuestionsAsAdmin(1, fetchAllLimit);
      setAllQuestions(response.data.items);
    },
    {
      requestKey: 'admin-questions-all',
      onError: (err) => {
        const errorMessage = '載入題目失敗，請稍後再試';
        showMessage(errorMessage, MessageType.Error);
        console.error('Error loading questions:', err);
      },
    },
  );

  // Use useApiRequest for modules fetching
  const { execute: loadModules } = useApiRequest(
    async () => {
      const response = await fetchAllQuestionModulesAsAdmin();
      setModules(response.data.items);
      // 如果沒有選擇模組且有可用模組，預設選擇第一個
      if (!selectedModuleId && response.data.items.length > 0) {
        const firstModule = response.data.items[0];
        if (firstModule) {
          setSelectedModuleId(firstModule.id);
          // 如果選擇的是sdgs模組，預設選擇第一個行業
          if (firstModule.name.toLowerCase() === 'sdgs') {
            const industries = Object.values(Industry);
            if (industries.length > 0) {
              const firstIndustry = industries[0];
              if (firstIndustry) {
                setSelectedIndustry(firstIndustry);
              }
            }
          }
        }
      }
    },
    {
      requestKey: 'admin-question-modules',
      onError: (err) => {
        console.error('Error loading modules:', err);
      },
    },
  );

  const loading = questionsLoading;
  const error = questionsError ? '載入題目失敗，請稍後再試' : null;

  useEffect(() => {
    loadQuestions();
    loadModules();
  }, []);

  const handleSave = async (
    questionData:
      AdminQuestion | Omit<AdminQuestion, 'id' | 'createdAt' | 'updatedAt'>,
  ) => {
    try {
      if (modalMode === 'edit' && 'id' in questionData) {
        // Update existing question
        const updateData: UpdateQuestionAsAdminRequest = {
          question: questionData.question,
          answerType: questionData.answerType,
          module:
            typeof questionData.module === 'string'
              ? questionData.module
              : questionData.module.name,
          information: questionData.information,
        };

        await updateQuestionAsAdmin(questionData.id, updateData);
        await loadQuestions();
        showMessage('題目更新成功', MessageType.Success);
      } else {
        // Create new question
        const createData: CreateQuestionAsAdminRequest = {
          question: questionData.question,
          answerType: questionData.answerType,
          module:
            typeof questionData.module === 'string'
              ? questionData.module
              : questionData.module.name,
          information: questionData.information || '',
          industryId: questionData.industry?.id,
          parentQuestionId: questionData.parentQuestionId,
        };

        await createQuestionAsAdmin(createData);
        // Reload questions to get the complete data from server
        await loadQuestions();
        showMessage('題目新增成功', MessageType.Success);
      }
    } catch (err) {
      const message =
        modalMode === 'edit'
          ? '題目更新失敗，請稍後再試'
          : '題目新增失敗，請稍後再試';
      showMessage(message, MessageType.Error);
      console.error(
        `Error ${modalMode === 'edit' ? 'updating' : 'creating'} question:`,
        err,
      );
    }
  };

  const getAnswerTypeLabel = (type: string) => {
    switch (type) {
      case AnswerType.Radio:
        return '單選題';
      case AnswerType.Textarea:
        return '問答題';
      case AnswerType.Select:
        return '下拉選單';
      case AnswerType.Info:
        return '資訊顯示';
      case AnswerType.Upload:
        return '檔案上傳';
      default:
        return type;
    }
  };

  const getAnswerTypeColor = (type: string) => {
    switch (type) {
      case AnswerType.Radio:
        return 'blue';
      case AnswerType.Textarea:
        return 'green';
      case AnswerType.Select:
        return 'purple';
      case AnswerType.Info:
        return 'orange';
      case AnswerType.Upload:
        return 'cyan';
      default:
        return 'default';
    }
  };

  const handleDeleteConfirm = (questionId: string) => {
    showConfirm(
      '確認刪除',
      '確定要刪除這個題目嗎？此操作無法復原。',
      async () => {
        try {
          setDeletingQuestion(questionId);
          await deleteQuestionAsAdmin(questionId);
          await loadQuestions();
          showMessage('題目刪除成功', MessageType.Success);
        } catch (err) {
          showMessage('題目刪除失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting question:', err);
        } finally {
          setDeletingQuestion(null);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const renderAnswerTypeTag = (answerType: string) => (
    <Tag color={getAnswerTypeColor(answerType)}>
      {getAnswerTypeLabel(answerType)}
    </Tag>
  );

  const renderActions = (question: AdminQuestion, label: string) => (
    <Space size={0}>
      <Button
        type="text"
        icon={<EditOutlined />}
        onClick={() => {
          setModalMode('edit');
          setEditing(question);
        }}
        title={`編輯${label}`}
        size="small"
      />
      <Button
        type="text"
        icon={<DeleteOutlined />}
        onClick={() => handleDeleteConfirm(question.id)}
        loading={deletingQuestion === question.id}
        title={`刪除${label}`}
        danger
        size="small"
      />
    </Space>
  );

  // 追問（子題）直接顯示在主題下方，可多層
  const renderFollowUps = (children?: AdminQuestionWithChildren[]) =>
    children?.map((child) => (
      <div
        key={child.id}
        style={{
          marginTop: 6,
          paddingLeft: 12,
          borderLeft: '2px solid #e8e8e8',
        }}
      >
        <Flex align="center" gap={8} wrap="wrap">
          <Text type="secondary">↳ 追問</Text>
          <Text>{child.question}</Text>
          {renderAnswerTypeTag(child.answerType)}
          {renderActions(child, '追問')}
        </Flex>
        {renderFollowUps(child.children)}
      </div>
    ));

  const questionColumns: ColumnsType<AdminQuestionWithChildren> = [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
      width: 56,
      render: (order: number) => <Text>{order}</Text>,
    },
    {
      title: '題目內容',
      dataIndex: 'question',
      key: 'question',
      render: (question: string, record) => (
        <div>
          <Text strong>{question}</Text>
          {renderFollowUps(record.children)}
        </div>
      ),
    },
    {
      title: '類型',
      dataIndex: 'answerType',
      key: 'answerType',
      width: 96,
      render: (answerType: string) => renderAnswerTypeTag(answerType),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 144,
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(updatedAt))}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, question) => renderActions(question, '題目'),
    },
  ];

  const handleModuleFilterChange = (value: string) => {
    setSelectedModuleId(value);
    setPage(1);

    // 如果選擇的是sdgs模組，預設選擇第一個行業
    const selectedModule = modules.find((m) => m.id === value);
    if (selectedModule?.name.toLowerCase() === 'sdgs') {
      const industries = Object.values(Industry);
      if (industries.length > 0) {
        const firstIndustry = industries[0];
        if (firstIndustry) {
          setSelectedIndustry(firstIndustry);
        }
      }
    } else {
      setSelectedIndustry(null);
    }
  };

  const handleIndustryFilterChange = (value: string) => {
    setSelectedIndustry(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="題目庫管理"
        description="管理所有問卷題目，建立和編輯問題內容。"
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex vertical gap={12}>
          <Flex align="center" gap={12} wrap="wrap">
            <Text strong style={{ minWidth: 40 }}>
              模組
            </Text>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={selectedModuleId}
              onChange={(e) => handleModuleFilterChange(e.target.value)}
              options={modules.map((module) => ({
                value: module.id,
                label: `${getModuleLabel(module.name)}（${moduleCounts.get(module.id) ?? 0}）`,
              }))}
            />
          </Flex>
          {isSDGsModule && (
            <Flex align="center" gap={12} wrap="wrap">
              <Text strong style={{ minWidth: 40 }}>
                產業
              </Text>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={selectedIndustry}
                onChange={(e) => handleIndustryFilterChange(e.target.value)}
                options={industries.map((industry) => ({
                  value: industry,
                  label: `${industry}（${industryCounts.get(industry) ?? 0}）`,
                }))}
              />
            </Flex>
          )}
        </Flex>
      </Card>

      <TableContainer
        title={
          selectedModule
            ? [
                getModuleLabel(selectedModule.name),
                isSDGsModule ? selectedIndustry : null,
              ]
                .filter(Boolean)
                .join(' › ')
            : '題目列表'
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setModalMode('add');
              setIsAddingQuestion(true);
              setEditing(null);
            }}
          >
            新增題目
          </Button>
        }
      >
        <StateRenderer
          loading={loading}
          error={error}
          empty={questions.length === 0}
          emptyMessage="開始建立您的第一個問題"
          emptyIcon={<QuestionCircleOutlined />}
          onRetry={loadQuestions}
        >
          <Table<AdminQuestionWithChildren>
            scroll={{ x: 'max-content' }}
            columns={questionColumns}
            dataSource={questions}
            rowKey="id"
            // 追問已在題目內容欄位內顯示，不使用表格的樹狀展開
            expandable={{ childrenColumnName: 'followUps' }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: questions.length,
              onChange: setPage,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 題，共 ${total} 題`,
            }}
            size="small"
            loading={loading}
          />
        </StateRenderer>
      </TableContainer>

      <QuestionModal
        isOpen={!!editing || isAddingQuestion}
        question={editing}
        mode={modalMode}
        onClose={() => {
          setEditing(null);
          setIsAddingQuestion(false);
        }}
        onSave={handleSave}
      />
    </>
  );
};

export default AdminQuestionsPage;
